export type PushDestinationType = 'none' | 'app_path' | 'entity' | 'web_url'

export type PushClickEventType = 'opened' | 'received_foreground'

export interface PushNotificationData {
  campaign_id?: string
  tracking_token?: string
  channel?: string
  destination_type?: PushDestinationType | string
  app_path?: string
  web_url?: string
  entity_type?: string
  entity_id?: string
  platform?: string
  sent_at?: string
}

export interface PushEntityRoute {
  pageName: string
  params?: Record<string, string>
}

const PUSH_APP_LOCALE = 'fr'

/** French app paths (i18n prefix strategy). Native push deep links use French only. */
const FRENCH_PAGE_PATHS: Record<string, string> = {
  FlyerPage: '/fr/circulaire/:id-:slug',
  FlyersStorePage: '/fr/circulaires/:slug',
  RecipePage: '/fr/recette/:id/:slug',
  ArticlePage: '/fr/article/:id/:slug',
  ExtraDiscountsPage: '/fr/rabais-extra',
  FlyersPage: '/fr/circulaires',
  RecipesPage: '/fr/recettes',
  ArticlesPage: '/fr/articles',
  ShoppingListPage: '/fr/liste-epicerie',
  MealPlannerPage: '/fr/planificateur',
  RecipeBoxPage: '/fr/boite-recettes',
  TrackedIngredientsPage: '/fr/produits-suivis',
  MoneySaverPage: '/fr/economiseur',
  BestItemsPage: '/fr/meilleurs-rabais',
  GasPricePage: '/fr/prix-essence',
  ExtraPage: '/fr/extra',
  UserProfilePage: '/fr/profil',
}

const ENTITY_ROUTE_MAP: Record<string, PushEntityRoute> = {
  flyer: { pageName: 'FlyerPage', params: { slug: 'notification' } },
  flyer_store: { pageName: 'FlyersStorePage' },
  recipe: { pageName: 'RecipePage', params: { slug: 'notification' } },
  blog_post: { pageName: 'ArticlePage', params: { slug: 'notification' } },
  article: { pageName: 'ArticlePage', params: { slug: 'notification' } },
  discount: { pageName: 'ExtraDiscountsPage' },
  extra_discount: { pageName: 'ExtraDiscountsPage' },
  flyers: { pageName: 'FlyersPage' },
  recipes: { pageName: 'RecipesPage' },
  articles: { pageName: 'ArticlesPage' },
  shopping_list: { pageName: 'ShoppingListPage' },
  meal_planner: { pageName: 'MealPlannerPage' },
  recipe_box: { pageName: 'RecipeBoxPage' },
  tracked_ingredients: { pageName: 'TrackedIngredientsPage' },
  money_saver: { pageName: 'MoneySaverPage' },
  best_items: { pageName: 'BestItemsPage' },
  gas_prices: { pageName: 'GasPricePage' },
  extra: { pageName: 'ExtraPage' },
  profile: { pageName: 'UserProfilePage' },
}

export function getPushAppLocale(): string {
  return PUSH_APP_LOCALE
}

function buildFrenchAppPath(pageName: string, params: Record<string, string> = {}): string | null {
  const template = FRENCH_PAGE_PATHS[pageName]

  if (!template) {
    return null
  }

  let path = template

  for (const [key, value] of Object.entries(params)) {
    path = path.replace(`:${key}`, value)
  }

  if (path.includes(':')) {
    return null
  }

  return path
}

export function normalizePushData(data: Record<string, unknown> | undefined | null): PushNotificationData {
  if (!data) {
    return {}
  }

  return {
    campaign_id: stringifyPushField(data.campaign_id),
    tracking_token: stringifyPushField(data.tracking_token),
    channel: stringifyPushField(data.channel),
    destination_type: stringifyPushField(data.destination_type) as PushDestinationType | undefined,
    app_path: stringifyPushField(data.app_path),
    web_url: stringifyPushField(data.web_url),
    entity_type: stringifyPushField(data.entity_type),
    entity_id: stringifyPushField(data.entity_id),
    platform: stringifyPushField(data.platform),
    sent_at: stringifyPushField(data.sent_at),
  }
}

function stringifyPushField(value: unknown): string | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined
  }

  return String(value)
}

export function mapEntityTypeToRoute(
  entityType: string,
  entityId: string | undefined,
): string | null {
  const mapping = ENTITY_ROUTE_MAP[entityType]

  if (!mapping) {
    return null
  }

  const params: Record<string, string> = { ...mapping.params }

  if (entityId) {
    if (entityType === 'flyer_store') {
      params.slug = entityId
    }
    else {
      params.id = entityId
    }
  }

  return buildFrenchAppPath(mapping.pageName, params)
}

export function resolvePushNavigationTarget(data: PushNotificationData): string | null {
  const destinationType = data.destination_type ?? 'none'

  if (destinationType === 'none') {
    return null
  }

  if (destinationType === 'app_path' && data.app_path) {
    return data.app_path.startsWith('/') ? data.app_path : `/${data.app_path}`
  }

  if (destinationType === 'entity' && data.entity_type) {
    const entityRoute = mapEntityTypeToRoute(data.entity_type, data.entity_id)

    if (entityRoute) {
      return entityRoute
    }
  }

  if (data.web_url) {
    return data.web_url
  }

  return null
}

export async function trackPushClick(
  apiUrl: string,
  payload: {
    tracking_token: string
    event_type: PushClickEventType
    installation_uuid: string
  },
): Promise<void> {
  if (!payload.tracking_token) {
    return
  }

  try {
    await $fetch(`${apiUrl}/push/click`, {
      method: 'POST',
      body: payload,
    })
  }
  catch (error) {
    console.warn('Failed to track push notification click:', error)
  }
}

export async function handlePushNotificationAction(
  rawData: Record<string, unknown> | undefined | null,
  options: {
    apiUrl: string
    installationUuid: string
    navigate?: (target: string) => Promise<void> | void
    openExternalUrl?: (url: string) => Promise<void> | void
    eventType?: PushClickEventType
  },
): Promise<void> {
  const data = normalizePushData(rawData)

  if (data.tracking_token) {
    await trackPushClick(options.apiUrl, {
      tracking_token: data.tracking_token,
      event_type: options.eventType ?? 'opened',
      installation_uuid: options.installationUuid,
    })
  }

  const target = resolvePushNavigationTarget(data)

  if (!target) {
    return
  }

  if (target.startsWith('http://') || target.startsWith('https://')) {
    if (options.openExternalUrl) {
      await options.openExternalUrl(target)
    }
    return
  }

  if (options.navigate) {
    await options.navigate(target)
  }
}
